import type {
  OnboardingRequirement,
  OnboardingSessionConfig,
  RequirementConfirmations,
} from '@onefootprint/request-types';
import { redirect } from '@tanstack/react-router';
import {
  callOnHandoffComplete,
  getIdv2StoreData,
  isContinueOnMobile,
  isTransferSession,
  isTransferVisited,
  setIdv2StoreData,
} from '@/idv2/features/idv-to-idv2';
import type { ComponentsSdkRelay } from '@/idv2/features/idv-to-idv2/idv2-provider';
import type { FileRouteTypes } from '@/idv2/routes/routeTree.gen';
import checkIsInIframe from '@/idv2/utils/check-is-in-iframe';
import { getBasicDevice } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
import { shouldRedirectToV2 } from '@/idv2/utils/requirement-redirect';
import { trackCurrentRouteCompleted } from '@/idv2/utils/route-tracking';
import { UnsupportedRequirementError } from '@/idv2/utils/unsupported-requirement-error';

export const shouldWaitForComponentsSdk = (relay?: ComponentsSdkRelay) =>
  !!relay && !getIdv2StoreData()?.componentsSdkRelayCompleted;

const { logInfo } = getLogger({ name: 'idv2', location: 'onboarding-token-route' });

/** Converts underscores to dashes in a string type */
type UnderscoreToDash<S extends string> = S extends `${infer L}_${infer R}` ? `${L}-${UnderscoreToDash<R>}` : S;
/** Requirement kind with underscores replaced by dashes (e.g., 'collect_card_data' → 'collect-card-data') */
type RequirementKindPath = UnderscoreToDash<OnboardingRequirement['kind']>;
/** Onboarding child routes that can be navigated to based on requirement kind */
type OnboardingChildRoute = Extract<FileRouteTypes['fullPaths'], `/onboarding/$token/${RequirementKindPath}`>;

/**
 * Maps requirement kind to IDV2 route path. A kind missing here ends on the unsupported-requirement
 * screen; `confirm_verified_prefill` is missing on purpose, since only headless onboardings yield it.
 */
const requirementRouteMap: Partial<Record<OnboardingRequirement['kind'], OnboardingChildRoute>> = {
  collect_data: '/onboarding/$token/collect-data',
  collect_custom_data: '/onboarding/$token/collect-custom-data',
  collect_card_data: '/onboarding/$token/collect-card-data',
  liveness: '/onboarding/$token/liveness',
  link_bank_account: '/onboarding/$token/link-bank-account',
  collect_investor_profile: '/onboarding/$token/collect-investor-profile',
  select_conditional_branch: '/onboarding/$token/select-conditional-branch',
  collect_business_data: '/onboarding/$token/collect-business-data',
  create_business_onboarding: '/onboarding/$token/create-business-onboarding',
  collect_document: '/onboarding/$token/collect-document',
  register_auth_method: '/onboarding/$token/register-auth-method',
};

export const getRouteForRequirement = (kind: OnboardingRequirement['kind']): OnboardingChildRoute | undefined => {
  return requirementRouteMap[kind];
};

/** Check if we're at the layout route itself vs a specific child route */
export const isLayoutRoute = (pathname: string, token: string) =>
  pathname === `/onboarding/${token}` || pathname === `/onboarding/${token}/`;

/** Check if any document has prefer_upload setting (should upload first, then transfer) */
const getPreferUploadDoc = (requirements: OnboardingRequirement[]) =>
  requirements.find(r => r.kind === 'collect_document' && r.uploadSettings === 'prefer_upload');

/** Tracks if a document has been routed to during this transfer session.
 * Needed to avoid infinite redirect loops when a user opts to "Continue on mobile instead" for a prefer_upload document. */
let handoffDocRouted = false;
export const resetHandoffDocRouted = () => {
  handoffDocRouted = false;
};

const isDeviceTablet = (): boolean => getBasicDevice().type === 'tablet';
const isDeviceMobile = (): boolean => getBasicDevice().type === 'mobile';

/** Set by new-tab-transfer.tsx from the opener's device type, never by a QR-scanned session, so a
 * misread opener can't send a phone back into the QR loop. */
const isDesktopNewTabTransfer = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('source') === 'newtab';
  } catch {
    return false;
  }
};

/**
 * Determines if transfer should run before the current requirement, returning true broadly for liveness/document requirements.
 * The transfer route's getTransferMode further determines which flow to take and skips if no useful action applies.
 */
const shouldRunTransfer = (
  currentRequirement: OnboardingRequirement,
  allRequirements: OnboardingRequirement[],
): boolean => {
  const nextRequirementIsLiveness = currentRequirement.kind === 'liveness';
  const nextRequirementIsIdDoc = currentRequirement.kind === 'collect_document';

  // When running inside a new tab opened from transfer, skip transfer to avoid showing the QR/transfer screen again.
  // Desktop transfer sessions complete the handoff for all non-liveness requirements in handleLayoutRoute.
  if (isTransferSession()) {
    return false;
  }

  const continueOnMobile = isContinueOnMobile();

  // When running natively (not in an iframe) on mobile, we can register the passkey without transferring.
  // If we're on desktop, we should still attempt to transfer
  const isInIframe = checkIsInIframe();
  if (!isInIframe && isDeviceMobile()) {
    return false;
  }

  // Documents requiring mobile capture can never be completed on desktop,
  // so always route through transfer regardless of visited state or prefer_upload documents
  const currentRequirementForcesTransfer =
    currentRequirement.kind === 'collect_document' &&
    currentRequirement.uploadSettings === 'require_mobile_capture' &&
    !isDeviceMobile() &&
    !isDeviceTablet();

  // If the user has clicked on "Continue on mobile" button, we should allow them to continue with the transfer
  // even though transfer was already visited
  if (!continueOnMobile && isTransferVisited() && !currentRequirementForcesTransfer) {
    return false;
  }

  // If we have a custom document with upload settings set as "prefer_upload",
  // we should first request the user to upload the document before transferring.
  // Bypassed when continueOnMobile is true (user explicitly wants mobile).
  // TODO: should we allow this when appclip is supported?
  if (!continueOnMobile && getPreferUploadDoc(allRequirements) && !currentRequirementForcesTransfer) {
    return false;
  }

  return nextRequirementIsIdDoc || nextRequirementIsLiveness;
};

/** Handle layout route: redirect to appropriate child route or legacy fallback */
export const handleLayoutRoute = async (
  currentRequirement: OnboardingRequirement,
  token: string,
  config: OnboardingSessionConfig,
  {
    allRequirements,
    confirmations,
  }: { allRequirements?: OnboardingRequirement[]; confirmations?: RequirementConfirmations } = {},
) => {
  // Desktop new-tab transfer sessions are opened specifically for passkey (WebAuthn can't run in
  // iframe). After liveness completes, return to parent window for all remaining requirements.
  // Mobile tabs don't carry `source=newtab` — they are the capture surface and run as full
  // handoff sessions, like a phone reached via QR.
  if (isTransferSession() && isDesktopNewTabTransfer() && currentRequirement.kind !== 'liveness') {
    const device = getBasicDevice();
    logInfo('Completing handoff and returning to parent window for remaining requirements', {
      deviceType: device.type,
      osName: device.osName,
      browser: device.browser,
      requirementKind: currentRequirement.kind,
    });
    callOnHandoffComplete();
    return;
  }

  // Handoff sessions without the marker (QR phones, mobile openers' tabs) return prefer_upload
  // documents to the opener, where file upload is easier. Only bail once the phone has routed to a
  // doc already — before that, the user explicitly chose "Continue on mobile" for this doc.
  if (
    isTransferSession() &&
    !isDesktopNewTabTransfer() &&
    handoffDocRouted &&
    currentRequirement.kind === 'collect_document' &&
    currentRequirement.uploadSettings === 'prefer_upload'
    // only triggeres if there's been 1 doc already processed during this transfer session
    // works for cases with multiple custom doc prefer_upload because you return to desktop and click "Continue on mobile instead" once for each
  ) {
    logInfo('Completing handoff for prefer_upload document, returning to opener', {
      requirementKind: currentRequirement.kind,
      uploadSettings: currentRequirement.uploadSettings,
    });
    callOnHandoffComplete();
    return;
  }

  if (allRequirements && shouldRunTransfer(currentRequirement, allRequirements)) {
    logInfo('Redirecting to transfer route');
    throw redirect({
      to: '/onboarding/$token/transfer' as '/onboarding/$token',
      params: { token },
    });
  }

  const route = getRouteForRequirement(currentRequirement.kind);

  // Legacy idv renders only collect_document and liveness, so relaying any other kind loops or blanks.
  if (!route) {
    throw new UnsupportedRequirementError(currentRequirement.kind, config.orgId);
  }

  if (shouldRedirectToV2({ kind: currentRequirement.kind, flags: config.flags, orgId: config.orgId })) {
    // Deliberately unlogged: `Requirement determined` already reported the kind, and the child
    // route logs its own arrival. The legacy fallback below leaves idv2, so that one is logged.
    if (isTransferSession() && currentRequirement.kind === 'collect_document') {
      handoffDocRouted = true;
    }

    const search: Record<string, string> = {};
    if (currentRequirement.kind === 'liveness') {
      search.actionKind = 'add_primary';
    }
    if (confirmations) {
      search.confirmations = confirmations;
    }

    throw redirect({
      to: route,
      params: { token },
      search: Object.keys(search).length > 0 ? search : undefined,
    });
  }

  logInfo('Relaying to legacy IDV', { requirementKind: currentRequirement.kind });
  if (isTransferSession() && currentRequirement.kind === 'collect_document') {
    handoffDocRouted = true;
  }
  trackCurrentRouteCompleted();
  await setIdv2StoreData({ relayToIDV2: false });
};
